import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { KeyRound, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ApiKey } from '@/types/apiKey';
import { ApiKeyStatusBadge } from './ApiKeyStatusBadge';

interface ApiKeyTableProps {
  apiKeys: ApiKey[];
  onRevoke: (apiKey: ApiKey) => void;
}

/**
 * "há 2h", "ontem", "12 ago". O uso da chave é informação de reconhecimento,
 * não de auditoria fina — o que importa é saber se ela ainda é usada.
 */
export const formatRelativeDate = (dateString?: string | null) => {
  if (!dateString) return 'nunca usada';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';

  const diffInMs = Date.now() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) return 'agora';
  if (diffInHours < 24) return `há ${diffInHours}h`;
  if (diffInDays === 1) return 'ontem';
  if (diffInDays < 7) return `há ${diffInDays}d`;
  return format(date, 'dd MMM yyyy', { locale: getInterfaceDateLocale() });
};

export const formatExpiry = (apiKey: ApiKey) => {
  if (apiKey.status === 'revoked') {
    return `revogada em ${format(new Date(apiKey.revokedAt!), 'dd MMM yyyy', { locale: getInterfaceDateLocale() })}`;
  }

  const expiresAt = new Date(apiKey.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return '—';

  if (apiKey.status === 'expired') {
    return `expirou em ${format(expiresAt, 'dd MMM yyyy', { locale: getInterfaceDateLocale() })}`;
  }

  const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 30) return `expira em ${daysLeft}d`;

  return format(expiresAt, 'dd MMM yyyy', { locale: getInterfaceDateLocale() });
};

export function ApiKeyTable({ apiKeys, onRevoke }: ApiKeyTableProps) {
  useLanguage();
  return (
    <TooltipProvider>
      <div className="border border-[#E5E7EB] rounded-xl overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E7EB] hover:bg-transparent">
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">
                {translateInterfaceText("Nome")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">
                {translateInterfaceText("Chave")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">
                {translateInterfaceText("Status")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">
                {translateInterfaceText("Último uso")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">
                {translateInterfaceText("Validade")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide w-[80px]">
                {translateInterfaceText("Ações")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((apiKey) => (
              <TableRow
                key={apiKey.id}
                className="border-[#E5E7EB] transition-colors hover:bg-[#F8FAFC] group"
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <KeyRound className="w-4 h-4 text-[#437DFF]" />
                    </div>
                    <span className="text-sm text-[#111] font-medium truncate max-w-[220px]">
                      {apiKey.name}
                    </span>
                  </div>
                </TableCell>

                {/*
                  Só o prefixo, e sem botão de copiar: o resto da chave não
                  existe mais no banco. Copiar um prefixo não serviria para
                  nada e sugeriria que a chave inteira está aqui.
                */}
                <TableCell>
                  <span className="text-sm text-[#64748B] font-mono">
                    {apiKey.prefix}
                    <span className="text-[#CBD5E1]">••••••••</span>
                  </span>
                </TableCell>

                <TableCell>
                  <ApiKeyStatusBadge status={apiKey.status} />
                </TableCell>

                <TableCell>
                  <span className="text-sm text-[#64748B]">
                    {formatRelativeDate(apiKey.lastUsedAt)}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="text-sm text-[#64748B]">{formatExpiry(apiKey)}</span>
                </TableCell>

                <TableCell>
                  {apiKey.status === 'active' ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onRevoke(apiKey)}
                          aria-label={translateInterfaceText("Revogar a chave {value0}", { value0: (apiKey.name) })}
                          className="text-[#64748B] hover:text-red-600 transition-colors p-1.5 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{translateInterfaceText("Revogar chave")}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-sm text-[#CBD5E1]">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
